import React, { useEffect, useState } from 'react';
import { Sparkles, Shield, XCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';

/**
 * AIConsentModal — blocking modal shown before any AI feature can be used.
 *
 * Required by Apple App Store guidelines 5.1.1(i) / 5.1.2(i):
 *  - Explicitly discloses that data is sent to a third-party AI service
 *  - Names the recipient (OpenAI)
 *  - Lists the exact data types shared
 *  - Requires an explicit "I Agree" action before any AI call is made
 *  - Backed up by matching Privacy Policy language
 *
 * Behavior:
 *  - Mounted app-wide inside AppLayout
 *  - Queries GET /api/ai/consent/status once per authenticated session
 *  - If granted === false → renders as a blocking modal
 *  - If granted === true  → renders nothing
 *  - "Decline" closes the modal without granting; AI features remain blocked
 *    server-side (require_ai_consent). The user can grant later from Settings.
 */
const AIConsentModal = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null); // null = loading, object = fetched
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  // Load consent status when user becomes available.
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await api.get('/api/ai/consent/status');
        if (cancelled) return;
        setStatus(data);
        // Show modal if not granted yet, or if user's version is out of date.
        if (!data.granted || data.needs_reconsent) {
          setOpen(true);
        }
      } catch (err) {
        if (typeof window !== 'undefined' && window.console) {
          window.console.warn('AI consent status fetch failed', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleAgree = async () => {
    setSubmitting(true);
    try {
      await api.post('/api/ai/consent', { consent_version: '1.0' });
      setStatus({ ...(status || {}), granted: true, needs_reconsent: false });
      setOpen(false);
      toast.success('Thank you — AI features are ready to use.');
    } catch (err) {
      if (typeof window !== 'undefined' && window.console) {
        window.console.error('AI consent grant failed', err);
      }
      toast.error('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    // Do NOT record anything server-side — just close for this session.
    // Server continues to block AI endpoints via require_ai_consent.
    // User will see the modal again next session, or can grant from Settings.
    setDismissedThisSession(true);
    setOpen(false);
    toast('AI features remain disabled. You can enable them anytime in Settings.', {
      duration: 6000,
    });
  };

  if (!open || dismissedThisSession) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-consent-title"
      data-testid="ai-consent-modal"
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-[var(--sd-surface,#fdfbfa)] shadow-2xl border border-[var(--sd-border,#e8d7da)]">
        <div className="p-6 sm:p-8 space-y-6">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[var(--sd-accent-soft,#f5e6ea)] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-[var(--sd-accent,#b76e79)]" />
            </div>
            <div className="flex-1">
              <h2
                id="ai-consent-title"
                className="text-xl sm:text-2xl font-semibold text-[var(--sd-fg,#2a1f22)] leading-snug"
              >
                Selah uses AI to help you reflect
              </h2>
              <p className="mt-1 text-sm text-[var(--sd-fg-muted,#6b5a5f)]">
                Before we begin, here&apos;s exactly what we share and with whom.
              </p>
            </div>
          </div>

          {/* Disclosure body */}
          <div className="space-y-4 text-sm text-[var(--sd-fg,#2a1f22)]">
            <section>
              <h3 className="font-semibold mb-1.5 text-[var(--sd-fg,#2a1f22)]">
                What we send to OpenAI
              </h3>
              <ul className="pl-5 list-disc space-y-1 text-[var(--sd-fg-muted,#6b5a5f)]">
                <li>Voice recordings you make (for transcription)</li>
                <li>
                  The transcribed text of your reflections (for Scripture
                  matching and prayer generation)
                </li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold mb-1.5 text-[var(--sd-fg,#2a1f22)]">
                What OpenAI does with it
              </h3>
              <ul className="pl-5 list-disc space-y-1 text-[var(--sd-fg-muted,#6b5a5f)]">
                <li>Processes it solely to return results to you</li>
                <li>
                  Does <strong>not</strong> use it to train their AI models
                </li>
                <li>
                  Bound by their Enterprise API Data Processing Agreement
                </li>
                <li>Retains it briefly for abuse monitoring, then deletes</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold mb-1.5 text-[var(--sd-fg,#2a1f22)]">
                What Selah stores
              </h3>
              <ul className="pl-5 list-disc space-y-1 text-[var(--sd-fg-muted,#6b5a5f)]">
                <li>Only the final results (transcription, Scripture, prayer)</li>
                <li>Encrypted at rest in your private journal</li>
                <li>Permanently deleted when you delete your account</li>
              </ul>
            </section>

            {expanded && (
              <section className="rounded-lg bg-[var(--sd-surface-muted,#f9f2f3)] p-4 border border-[var(--sd-border,#e8d7da)]">
                <h3 className="font-semibold mb-1.5 text-[var(--sd-fg,#2a1f22)] flex items-center gap-1.5">
                  <Shield className="w-4 h-4" />
                  Your ongoing rights
                </h3>
                <ul className="pl-5 list-disc space-y-1 text-[var(--sd-fg-muted,#6b5a5f)] text-xs">
                  <li>
                    Revoke this consent anytime in Settings → Privacy & AI.
                    Voice capture continues to work locally; AI features
                    become disabled.
                  </li>
                  <li>
                    Delete your entire account and all data with one tap in
                    Settings.
                  </li>
                  <li>
                    Read the full details, including our list of every
                    third-party processor, in our{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </li>
                </ul>
              </section>
            )}

            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-[var(--sd-accent,#b76e79)] hover:underline inline-flex items-center gap-1"
              data-testid="ai-consent-learn-more"
            >
              <ExternalLink className="w-3 h-3" />
              {expanded ? 'Show less' : 'Learn more'}
            </button>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleDecline}
              disabled={submitting}
              className="flex-1 border-[var(--sd-border,#e8d7da)] text-[var(--sd-fg-muted,#6b5a5f)]"
              data-testid="ai-consent-decline"
            >
              <XCircle className="w-4 h-4 mr-1.5" />
              Decline
            </Button>
            <Button
              onClick={handleAgree}
              disabled={submitting}
              className="flex-1 bg-[var(--sd-accent,#b76e79)] hover:bg-[var(--sd-accent-hover,#a55f6a)] text-white shadow-sm"
              data-testid="ai-consent-agree"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              {submitting ? 'Saving…' : 'I Agree and Continue'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIConsentModal;
